import { useTranslation } from "react-i18next"

import type { FieldCitation, FieldValue, PlanSource } from "../../types"

import { cn } from "@/lib/utils"

import {
  citationAnchorId,
  fieldIsEmpty,
  fieldToLines,
  isNs,
  resolveCitationIndex,
} from "./plan-utils"
import { safeHref } from "../../utils/safe-href"

/** Amber "NS" chip for a not-specified sub-field. */
export function NsBadge({ className }: { className?: string }) {
  const { t } = useTranslation()
  return (
    <span
      title={t("segmentAnalysis.plan.notSpecified")}
      className={cn(
        "bg-warning/15 text-warning-foreground inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase",
        className
      )}
    >
      {t("segmentAnalysis.plan.ns")}
    </span>
  )
}

/** Origin/status chip (AI · Grounded / Manual). */
export function OriginBadge({ field }: { field: FieldValue }) {
  const { t } = useTranslation()
  if (field.origin === "manual") {
    return (
      <span className="bg-muted text-muted-foreground inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase">
        {t("segmentAnalysis.plan.manual")}
      </span>
    )
  }
  return (
    <span
      className={cn(
        "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase",
        field.status === "grounded"
          ? "bg-success/15 text-success"
          : "bg-secondary/40 text-foreground/70"
      )}
    >
      {field.status === "grounded"
        ? t("segmentAnalysis.plan.grounded")
        : t("segmentAnalysis.plan.aiGenerated")}
    </span>
  )
}

/**
 * Inline citation pills. In the Document view an `onCitationClick` handler scrolls
 * + flashes the matching Sources entry; in the Canvas view no handler is passed so
 * the chips render inert (the Document view owns Sources).
 */
export function CitationChips({
  citations,
  onCitationClick,
  sources = [],
}: {
  citations: FieldCitation[]
  onCitationClick?: (citation: FieldCitation) => void
  /** Global (deduplicated) source list — chips number by position in it. */
  sources?: PlanSource[]
}) {
  if (citations.length === 0) return null
  return (
    <span className="ml-1 inline-flex flex-wrap gap-1 align-middle">
      {citations.map((citation, i) => {
        // Keep the resolved index separate from the display label. When the
        // resolver returns null the citation cannot be located in the global
        // list, so the chip must render inert — it previously fell back to a
        // local number AND stayed clickable, giving a button whose handler had
        // nothing to scroll to (review P3).
        const resolved = resolveCitationIndex(citation, sources)
        const label = resolved ?? citation.index ?? i + 1
        const title = citation.title ?? citation.quote ?? citation.url ?? `Source ${label}`
        if (!onCitationClick || resolved === null) {
          return (
            <span
              key={`${label}-${i}`}
              className="bg-secondary/40 text-foreground/70 inline-flex h-4 min-w-4 items-center justify-center rounded px-1 text-[10px] font-semibold"
              title={title}
            >
              {label}
            </span>
          )
        }
        return (
          <button
            key={`${label}-${i}`}
            className="bg-secondary/40 text-foreground/70 hover:bg-secondary hover:text-foreground -my-1 inline-flex h-6 min-w-6 items-center justify-center rounded px-1 text-[10px] font-semibold transition"
            title={title}
            type="button"
            onClick={() => onCitationClick(citation)}
          >
            {label}
          </button>
        )
      })}
    </span>
  )
}

/** Renders a FieldValue as prose or a bullet list, with an NS placeholder + citations. */
export function FieldValueView({
  field,
  onCitationClick,
  sources,
}: {
  field: FieldValue
  onCitationClick?: (citation: FieldCitation) => void
  sources?: PlanSource[]
}) {
  const { t } = useTranslation()
  if (isNs(field) || fieldIsEmpty(field)) {
    return (
      <p className="text-muted-foreground flex items-center gap-2 text-sm italic">
        {t("segmentAnalysis.plan.notSpecified")}
        <NsBadge />
      </p>
    )
  }
  const lines = fieldToLines(field)
  const isList = Array.isArray(field.value)
  if (isList) {
    return (
      <ul className="text-foreground grid gap-1.5 text-sm">
        {lines.map((line, i) => (
          <li key={`${line}-${i}`} className="pl-3 before:mr-2 before:content-['–']">
            {line}
            {i === lines.length - 1 ? (
              <CitationChips
                citations={field.citations}
                sources={sources}
                onCitationClick={onCitationClick}
              />
            ) : null}
          </li>
        ))}
      </ul>
    )
  }
  return (
    <div className="text-foreground space-y-2 text-sm leading-6">
      {lines.map((para, i) => (
        <p key={`${para}-${i}`}>
          {para}
          {i === lines.length - 1 ? (
            <CitationChips
              citations={field.citations}
              sources={sources}
              onCitationClick={onCitationClick}
            />
          ) : null}
        </p>
      ))}
    </div>
  )
}

/** Document-view Sources list. Each entry carries a scroll-target id for citation chips. */
export function SourcesList({ planKind, sources }: { planKind: string; sources: PlanSource[] }) {
  const { t } = useTranslation()
  if (sources.length === 0) return null
  return (
    <section aria-label={t("segmentAnalysis.plan.sources")} className="mt-6">
      <h3 className="text-muted-foreground mb-2 text-xs font-semibold tracking-[0.12em] uppercase">
        {t("segmentAnalysis.plan.sources")}
      </h3>
      <ol className="grid gap-1.5">
        {sources.map((source, i) => {
          const index = i + 1
          // Citation URLs round-trip through user-editable plan fields, so an
          // attacker-supplied javascript:/data: URL could otherwise be stored
          // and clicked by another viewer (stored XSS). Non-http(s) degrades to
          // the plain-text branch rather than a dead link.
          const href = safeHref(source.url)
          return (
            <li
              key={source.id ?? source.sourceId ?? `${source.url}-${i}`}
              className="border-border bg-card scroll-mt-24 rounded-md border px-3 py-2 text-sm transition-colors"
              id={citationAnchorId(planKind, "sources", index)}
            >
              <span className="text-muted-foreground mr-2 font-semibold">[{index}]</span>
              {href ? (
                <a
                  className="text-primary underline-offset-2 hover:underline"
                  href={href}
                  rel="noreferrer"
                  target="_blank"
                >
                  {source.title ?? source.url}
                </a>
              ) : (
                <span className="text-foreground">
                  {source.title ?? source.sourceId ?? "Source"}
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </section>
  )
}
